import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiPackage, FiAlertCircle, FiCheckCircle, FiDollarSign, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import AnimatedCounter from '../components/AnimatedCounter';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryMap, setCategoryMap] = useState({});
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [stockLevelFilter, setStockLevelFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [stockableFilter, setStockableFilter] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [stats, setStats] = useState({
    totalProducts: 0,
    inStock: 0,
    lowStock: 0,
    outOfStock: 0,
    totalValue: 0,
    averagePrice: 0
  });
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    loadProducts();
  }, [selectedHierarchy]);

  const loadProducts = async () => {
    if (!selectedHierarchy?.outlet?.id) return;
    
    try {
      setLoading(true);
      
      // Load categories filtered by outlet
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categoriesData = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCategories(categoriesData);
      
      // Create category map
      const map = {};
      categoriesData.forEach(cat => {
        map[cat.id] = cat.name;
      });
      setCategoryMap(map);
      
      // Load stock data filtered by outlet (include both outletId matches and null outletId)
      const stockQuery1 = query(collection(db, 'stock'), where('outletId', '==', selectedHierarchy.outlet.id));
      const stockQuery2 = query(collection(db, 'stock'), where('outletId', '==', null));
      
      const [stockSnapshot1, stockSnapshot2] = await Promise.all([
        getDocs(stockQuery1),
        getDocs(stockQuery2)
      ]);
      
      const stockData = {};
      [...stockSnapshot1.docs, ...stockSnapshot2.docs].forEach(doc => {
        const data = doc.data();
        const productId = data.productId;
        
        if (!stockData[productId]) {
          stockData[productId] = 0;
        }
        stockData[productId] += (data.quantity || 0);
      });
      setStockMap(stockData);
      
      // Set up real-time listener for products
      const productsQuery = query(
        collection(db, 'products'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      
      const unsubscribeProducts = onSnapshot(productsQuery, (snapshot) => {
        const productsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(productsData);

        // Calculate stats using stock from stockMap
        const totalProducts = productsData.length;
        const inStock = productsData.filter(p => (stockData[p.id] || 0) > 10).length;
        const lowStock = productsData.filter(p => (stockData[p.id] || 0) > 0 && (stockData[p.id] || 0) <= 10).length;
        const outOfStock = productsData.filter(p => (stockData[p.id] || 0) === 0).length;
        const totalValue = productsData.reduce((sum, p) => sum + ((p.price || 0) * (stockData[p.id] || 0)), 0);
        const averagePrice = totalProducts > 0 ? productsData.reduce((sum, p) => sum + (p.price || 0), 0) / totalProducts : 0;

        setStats({
          totalProducts,
          inStock,
          lowStock,
          outOfStock,
          totalValue,
          averagePrice
        });

        setLoading(false);
      });

      // Return cleanup function
      return () => {
        unsubscribeProducts();
      };
    } catch (error) {
      console.error('Error loading products:', error);
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredProducts = products.filter(product => {
    const stock = stockMap[product.id] || 0;
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || product.categoryId === categoryFilter;
    const matchesStockLevel = !stockLevelFilter || 
      (stockLevelFilter === 'in-stock' && stock > 10) ||
      (stockLevelFilter === 'low-stock' && stock > 0 && stock <= 10) ||
      (stockLevelFilter === 'out-of-stock' && stock === 0);
    const matchesActive = !activeFilter || 
      (activeFilter === 'active' && product.active !== false) ||
      (activeFilter === 'inactive' && product.active === false);
    const matchesStockable = !stockableFilter ||
      (stockableFilter === 'stockable' && product.stockable !== false) ||
      (stockableFilter === 'non-stockable' && product.stockable === false);
    
    return matchesSearch && matchesCategory && matchesStockLevel && matchesActive && matchesStockable;
  }).sort((a, b) => {
    const stock_a = stockMap[a.id] || 0;
    const stock_b = stockMap[b.id] || 0;
    
    let aVal, bVal;
    
    if (sortField === 'name') {
      aVal = (a.name || '').toLowerCase();
      bVal = (b.name || '').toLowerCase();
    } else if (sortField === 'category') {
      aVal = (categoryMap[a.categoryId] || '').toLowerCase();
      bVal = (categoryMap[b.categoryId] || '').toLowerCase();
    } else if (sortField === 'price') {
      aVal = a.price || 0;
      bVal = b.price || 0;
    } else if (sortField === 'stock') {
      aVal = stock_a;
      bVal = stock_b;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setStockLevelFilter('');
    setActiveFilter('');
    setStockableFilter('');
  };

  const filtersActive = searchTerm || categoryFilter || stockLevelFilter || activeFilter || stockableFilter;

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Products</h1>
        <p className="page-subtitle">Manage your product inventory</p>
      </div>

      {/* Stats Widgets */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#2196F3' }}>
                <FiPackage className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalProducts} />
              </div>
              <div className="stat-card-label">Total Products</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#48bb78' }}>
                <FiCheckCircle className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.inStock} />
              </div>
              <div className="stat-card-label">In Stock</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ffa726' }}>
                <FiAlertCircle className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.lowStock} />
              </div>
              <div className="stat-card-label">Low Stock</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#ff6b6b' }}>
                <FiAlertCircle className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.outOfStock} />
              </div>
              <div className="stat-card-label">Out of Stock</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#00bcd4' }}>
                <FiDollarSign className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.totalValue} decimals={2} prefix="$" />
              </div>
              <div className="stat-card-label">Total Value</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-sm-6 col-xl-2">
          <div className="card stat-card hover-lift">
            <div className="card-body">
              <div className="stat-card-icon" style={{ background: '#667eea' }}>
                <FiDollarSign className="text-white" />
              </div>
              <div className="stat-card-value">
                <AnimatedCounter value={loading ? 0 : stats.averagePrice} decimals={2} prefix="$" />
              </div>
              <div className="stat-card-label">Avg Price</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card hover-lift">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Products</h5>
          <button className="btn btn-sm btn-primary">
            <FiPlus className="me-2" />
            Add Product
          </button>
        </div>
        
        {/* Filter Controls */}
        {!loading && products.length > 0 && (
          <div className="card-body border-bottom">
            <div className="row g-3 align-items-end">
              <div className="col-md-3">
                <label className="form-label small text-muted mb-1">Search</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Category</label>
                <select
                  className="form-select form-select-sm"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                >
                  <option value="">All Categories</option>
                  {categories
                    .filter(cat => cat.name)
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    .map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Stock Level</label>
                <select
                  className="form-select form-select-sm"
                  value={stockLevelFilter}
                  onChange={(e) => setStockLevelFilter(e.target.value)}
                >
                  <option value="">All Levels</option>
                  <option value="in-stock">In Stock (&gt;10)</option>
                  <option value="low-stock">Low Stock (1-10)</option>
                  <option value="out-of-stock">Out of Stock (0)</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Active</label>
                <select
                  className="form-select form-select-sm"
                  value={activeFilter}
                  onChange={(e) => setActiveFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="col-md-2">
                <label className="form-label small text-muted mb-1">Stockable</label>
                <select
                  className="form-select form-select-sm"
                  value={stockableFilter}
                  onChange={(e) => setStockableFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="stockable">Stockable</option>
                  <option value="non-stockable">Non-Stockable</option>
                </select>
              </div>
              <div className="col-md-1">
                <button
                  className="btn btn-sm btn-outline-secondary w-100"
                  onClick={handleClearFilters}
                  disabled={!filtersActive}
                >
                  <i className="bi bi-x-circle me-1"></i>
                  Clear
                </button>
              </div>
            </div>
            {filtersActive && (
              <div className="mt-2">
                <small className="text-muted">
                  Showing {filteredProducts.length} of {products.length} products
                </small>
              </div>
            )}
          </div>
        )}

        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted mt-2">Loading products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-5">
              <FiPackage size={48} className="text-muted mb-3" />
              <p className="text-muted mb-0">
                {searchTerm ? 'No products match your search.' : 'No products found.'}
              </p>
              {searchTerm && (
                <button
                  className="btn btn-sm btn-outline-primary mt-2"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('name')}
                    >
                      Product Name
                      {sortField === 'name' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('category')}
                    >
                      Category
                      {sortField === 'category' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('price')}
                    >
                      Price
                      {sortField === 'price' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleSort('stock')}
                    >
                      Stock
                      {sortField === 'stock' && (
                        sortDirection === 'asc' ? <FiArrowUp className="ms-1" /> : <FiArrowDown className="ms-1" />
                      )}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map(product => {
                    const stock = stockMap[product.id] || 0;
                    return (
                      <tr key={product.id}>
                        <td className="fw-semibold">{product.name}</td>
                        <td>{categoryMap[product.categoryId] || product.category || '-'}</td>
                        <td>{product.price?.toFixed(2) || '0.00'}</td>
                        <td>
                          <span className={`badge ${stock > 10 ? 'bg-success' : stock > 0 ? 'bg-warning' : 'bg-danger'}`}>
                            {stock}
                          </span>
                        </td>
                        <td>
                          <button className="btn btn-sm btn-outline-primary me-2">
                            <FiEdit2 />
                          </button>
                          <button className="btn btn-sm btn-outline-danger">
                            <FiTrash2 />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
